import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { api, ConflictError, SessionExpired, type Meta, type Ticket, type TicketDetail as Detail } from '../api';
import { Markdown } from '../components/Markdown';
import { Comments } from './Comments';
import { Links } from './Links';

const FIELDS: { key: keyof Ticket; label: string; options: keyof Meta }[] = [
  { key: 'status', label: 'Status', options: 'statuses' },
  { key: 'epic', label: 'Epic', options: 'epics' },
  { key: 'planPriority', label: 'Plan priority', options: 'planPriorities' },
  { key: 'sequencing', label: 'Sequencing', options: 'sequencings' },
  { key: 'effort', label: 'Effort', options: 'efforts' },
];

function Activity({ entries }: { entries: Detail['activity'] }) {
  if (entries.length === 0) return null;
  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        Activity
      </Typography>
      <Stack spacing={0.75} sx={{ mt: 0.5 }}>
        {[...entries].reverse().map((entry) => (
          <Stack key={entry.id} direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" sx={{ minWidth: 78 }} color="text.secondary">
              {new Date(entry.at).toLocaleDateString()}
            </Typography>
            <Typography variant="body2">
              <strong>{entry.action}</strong>
              {entry.changes ? ` · ${Object.keys(entry.changes).join(', ')}` : ''}
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              label={entry.actor}
              color={entry.actorKind === 'token' ? 'primary' : 'default'}
            />
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

function FieldBar({
  ticket,
  meta,
  disabled,
  onChange,
}: {
  ticket: Ticket;
  meta: Meta | null;
  disabled: boolean;
  onChange: (fields: Partial<Ticket>) => void;
}) {
  return (
    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
      {FIELDS.map((field) => {
        const value = String(ticket[field.key] ?? '');
        const options = meta?.[field.options] ?? [];
        // A Select whose value is absent from its own options renders blank and warns.
        // Before deep links this never showed: the drawer could only be opened by
        // clicking a card, long after /v1/meta had landed. Arriving directly at a ticket
        // opens it on the first render, with every one of these five fields holding a
        // real value and no options yet — five warnings and five blank controls.
        const shown = value && !options.includes(value) ? [value, ...options] : options;
        return (
          <TextField
            key={field.key}
            select
            size="small"
            label={field.label}
            sx={{ minWidth: 160 }}
            value={value}
            disabled={disabled || !meta}
            onChange={(event) => onChange({ [field.key]: event.target.value })}
          >
            {shown.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        );
      })}
    </Stack>
  );
}

function BodyEditor({
  draft,
  saving,
  onChange,
  onSave,
  onCancel,
}: {
  draft: string;
  saving: boolean;
  onChange: (next: string) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <Stack spacing={1.5}>
      <TextField
        multiline
        minRows={14}
        value={draft}
        onChange={(event) => onChange(event.target.value)}
        slotProps={{
          input: { sx: { fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 13 } },
        }}
      />
      <Stack direction="row" spacing={1}>
        <Button variant="contained" size="small" disabled={saving} onClick={onSave}>
          {saving ? 'Saving\u2026' : 'Save'}
        </Button>
        <Button size="small" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
}

/**
 * A conflict is shown, never resolved automatically. Their version is rendered so the
 * edit can be reconciled by eye; the draft behind this notice is untouched either way.
 */
function ConflictNotice({
  conflict,
  onOverwrite,
}: {
  conflict: ConflictError;
  onOverwrite: () => void;
}) {
  return (
    <Alert
      severity="warning"
      action={
        <Button size="small" onClick={onOverwrite}>
          Overwrite
        </Button>
      }
    >
      <AlertTitle>Someone else changed this ticket</AlertTitle>
      {conflict.message} Your draft is untouched — review theirs below, then overwrite or copy
      your changes across.
      <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1, maxHeight: 200, overflow: 'auto' }}>
        <Markdown>
          {('body' in conflict.current && conflict.current.body) ||
            '_(their version has no body)_'}
        </Markdown>
      </Box>
    </Alert>
  );
}

/**
 * `alignItems` is deliberately NOT `flex-start` on the Stack: that sizes every child to
 * its own content, which is what let a wide table grow the body past the panel. Only the
 * button shrinks to fit; the markdown stretches to the column and scrolls inside it.
 */
function ReadPane({ body, onEdit }: { body: string; onEdit: () => void }) {
  return (
    <Stack spacing={1}>
      <Button size="small" onClick={onEdit} sx={{ alignSelf: 'flex-start' }}>
        Edit
      </Button>
      {body ? (
        <Markdown>{body}</Markdown>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No description yet.
        </Typography>
      )}
    </Stack>
  );
}

/**
 * The close button is not decoration. Expanded, the drawer is the full viewport, so the
 * Modal backdrop is entirely covered and clicking outside is impossible — Esc would be
 * the only exit, and nothing on screen says so. `ConnectAgent` carries one for the same
 * reason.
 */
function Header({
  ticket,
  expanded,
  onToggleExpand,
  onClose,
}: {
  ticket: Ticket;
  expanded: boolean;
  onToggleExpand: () => void;
  onClose: () => void;
}) {
  return (
    <>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="overline" color="primary">
            DWLF-{ticket.ref} {ticket.archived && '· archived'}
          </Typography>
          <Typography variant="h5" fontWeight={600}>
            {ticket.name}
          </Typography>
        </Box>
        <Tooltip title={expanded ? 'Exit full screen' : 'Full screen'}>
          {/* Below `sm` the panel is already the full viewport and both measures exceed
              it, so the toggle would change nothing but its own icon. */}
          <IconButton
            size="small"
            onClick={onToggleExpand}
            sx={{ mt: 0.5, display: { xs: 'none', sm: 'inline-flex' } }}
          >
            {expanded ? (
              <CloseFullscreenIcon fontSize="small" />
            ) : (
              <OpenInFullIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose} sx={{ mt: 0.5 }} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Box>
        <Typography variant="overline" color="text.secondary">
          Revenue consequence
        </Typography>
        <Typography variant="body2">{ticket.revenueConsequence}</Typography>
      </Box>
    </>
  );
}

/** The records hanging off a ticket: what was said, what it connects to, what happened. */
function RelatedRecords({
  detail,
  onDetailChange,
  onReload,
}: {
  detail: Detail;
  onDetailChange: (next: Detail) => void;
  onReload: () => void;
}) {
  return (
    <>
      <Divider />

      <Comments
        ticketRef={detail.ticket.ref}
        comments={detail.comments}
        onAdded={(comment) =>
          onDetailChange({ ...detail, comments: [...detail.comments, comment] })
        }
      />

      <Divider />

      {/* Reloads rather than patching local state: the server writes the inverse link on
          the OTHER ticket, so the only honest source for what exists now is the server. */}
      <Links ticketRef={detail.ticket.ref} links={detail.links} onChanged={onReload} />

      <Divider />
      <Activity entries={detail.activity} />
    </>
  );
}

/**
 * A failed load used to render the spinner FOREVER: `problem` was set, but the alert that
 * shows it lives inside the loaded view, so it could never be reached — and with no header
 * there was no close button either, leaving the browser's back button as the only way out.
 *
 * Nearly unreachable before, because the only way to open a ticket was to click a card
 * that demonstrably existed. A URL anyone can type, mistype, or keep after the ticket is
 * archived makes it ordinary — so it has to say what happened and offer the way back.
 */
function LoadFailure({
  refNumber,
  problem,
  onClose,
}: {
  refNumber: number;
  problem: string;
  onClose: () => void;
}) {
  return (
    <Stack spacing={2} sx={{ p: 3 }}>
      <Alert
        severity="error"
        action={
          <Button size="small" onClick={onClose}>
            Back to the board
          </Button>
        }
      >
        <AlertTitle>DWLF-{refNumber} could not be opened</AlertTitle>
        {/* The server's message is the specific one — "DWLF-99999 does not exist." Adding
            a guess after it both repeated that and was wrong: an archived ticket still
            loads, so "may have been archived" pointed at a cause that cannot happen. */}
        {problem}
      </Alert>
    </Stack>
  );
}

/** Everything that can go wrong with a save, and nothing that can go wrong with a load. */
function SaveProblems({
  conflict,
  problem,
  onOverwrite,
  onDismiss,
}: {
  conflict: ConflictError | null;
  problem: string | null;
  onOverwrite: () => void;
  onDismiss: () => void;
}) {
  return (
    <>
      {conflict && <ConflictNotice conflict={conflict} onOverwrite={onOverwrite} />}
      {problem && (
        <Alert severity="error" onClose={onDismiss}>
          {problem}
        </Alert>
      )}
    </>
  );
}

/**
 * The save half of a ticket: the draft, the in-flight flag, and the three ways a write can
 * fail. Split out because it was most of TicketDetail's body and none of its subject —
 * TicketDetail decides what to SHOW, this decides what happens when you save.
 */
function useTicketSave({
  refNumber,
  detail,
  setDetail,
  onSaved,
}: {
  refNumber: number;
  detail: Detail | null;
  setDetail: (next: Detail) => void;
  onSaved: (ticket: Ticket) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const [conflict, setConflict] = useState<ConflictError | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // A different ticket in the drawer starts clean — including its draft, which belongs
    // to the ticket it was typed into and must not follow you to the next one.
    //
    // Keyed on `refNumber`, the thing that actually changed, rather than on the ref that
    // finished LOADING: the latter only works while the caller happens to clear `detail`
    // first, and would leave a draft from the previous ticket in the editor if the next
    // one failed to load.
    setDraft(null);
    setConflict(null);
    setProblem(null);
  }, [refNumber]);

  async function run(fields: Partial<Ticket>, version: number) {
    if (!detail) return;
    setSaving(true);
    setProblem(null);
    try {
      const result = await api.update(detail.ticket.ref, version, fields);
      setDetail({ ...detail, ticket: result.ticket });
      setDraft(null);
      setConflict(null);
      onSaved(result.ticket);
    } catch (cause) {
      // The draft is deliberately NOT cleared on any of these paths. A 409, an expired
      // session or a validation failure must never be able to eat unsaved markdown.
      if (cause instanceof ConflictError) setConflict(cause);
      else if (cause instanceof SessionExpired)
        setProblem('Session expired — sign in again; your draft is safe.');
      else setProblem(cause instanceof Error ? cause.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  return { draft, setDraft, conflict, problem, setProblem, saving, run };
}

interface Props {
  refNumber: number;
  meta: Meta | null;
  expanded: boolean;
  onToggleExpand: () => void;
  onClose: () => void;
  onSaved: (ticket: Ticket) => void;
}

export function TicketDetail({
  refNumber,
  meta,
  expanded,
  onToggleExpand,
  onClose,
  onSaved,
}: Props) {
  const [detail, setDetail] = useState<Detail | null>(null);
  const [loadProblem, setLoadProblem] = useState<string | null>(null);
  const save = useTicketSave({ refNumber, detail, setDetail, onSaved });

  /**
   * Which load is current. Before deep links, changing ticket meant closing the drawer,
   * which unmounts this component — so a late response landed on a dead one and did
   * nothing. Back and Forward now change `refNumber` in place: go Back to DWLF-12 and
   * Forward to DWLF-93 before 12's GET resolves, and 12's response would arrive last and
   * overwrite 93 — leaving the panel showing one ticket while the URL, `refNumber` and
   * every subsequent comment or link belong to another.
   */
  const currentLoad = useRef(0);

  const reload = useCallback(() => {
    const token = (currentLoad.current += 1);
    const isCurrent = () => token === currentLoad.current;
    api
      .get(refNumber)
      .then((loaded) => {
        if (isCurrent()) setDetail(loaded);
      })
      .catch((cause: unknown) => {
        if (isCurrent()) {
          setLoadProblem(cause instanceof Error ? cause.message : 'Could not load this ticket.');
        }
      });
  }, [refNumber]);

  useEffect(() => {
    setDetail(null);
    setLoadProblem(null);
    reload();
  }, [refNumber, reload]);

  if (!detail) {
    if (!loadProblem) return <CircularProgress sx={{ m: 3 }} />;
    return <LoadFailure refNumber={refNumber} problem={loadProblem} onClose={onClose} />;
  }
  const { ticket } = detail;
  // A const local so the null check below narrows inside the callbacks too. Reading
  // `save.draft` there loses the narrowing, which is the only reason an earlier version
  // carried a `?? ''` — a fallback that would have silently replaced the body with an
  // empty string, the exact thing useTicketSave goes out of its way to prevent.
  const draft = save.draft;

  // Expanded widens the measure rather than removing it — prose set across a 2560px
  // monitor is not more readable, it is less. `mx` is a no-op in the narrow drawer,
  // where maxWidth already exceeds the panel.
  return (
    <Stack spacing={2} sx={{ p: 3, maxWidth: expanded ? 1400 : 900, mx: 'auto' }}>
      <Header
        ticket={ticket}
        expanded={expanded}
        onToggleExpand={onToggleExpand}
        onClose={onClose}
      />

      <FieldBar
        ticket={ticket}
        meta={meta}
        disabled={save.saving}
        onChange={(fields) => void save.run(fields, ticket.version)}
      />

      <SaveProblems
        conflict={save.conflict}
        problem={save.problem}
        onOverwrite={() =>
          save.conflict &&
          void save.run({ body: draft ?? ticket.body }, save.conflict.currentVersion)
        }
        onDismiss={() => save.setProblem(null)}
      />

      <Divider />

      {draft === null ? (
        <ReadPane body={ticket.body} onEdit={() => save.setDraft(ticket.body)} />
      ) : (
        <BodyEditor
          draft={draft}
          saving={save.saving}
          onChange={save.setDraft}
          onSave={() => void save.run({ body: draft }, ticket.version)}
          onCancel={() => save.setDraft(null)}
        />
      )}

      <RelatedRecords detail={detail} onDetailChange={setDetail} onReload={reload} />
    </Stack>
  );
}
